// ══════════════════════════════════════════════════════════
//  #LIVE_KEYS_SHARED_V1 (2026-09-24) — ONE change-poller for the whole app
//
//  WHY: App.js, HRModule.js and PMDashboard.js each had their own copy of useLiveKeys, and EVERY
//  call site ran its own 12-second timer. A user sitting on the Owner Dash was firing 6–8 separate
//  "did anything change?" requests every 12s (customer-alias, job-overrides, revenue-input ×2,
//  cost-payroll-weekly, fleet-fuel-weekly, leadership-*, ...). Supabase logs 9/24: ~45,000 of these
//  a day for ~20 users.
//
//  NOW: every screen still calls useLiveKeys(keys, onChange, opts) exactly the same way — nothing
//  at the call sites changes. Under the hood they all register with ONE shared timer that asks for
//  the union of every watched key in a SINGLE request, then hands each screen only its own changes.
//  Same behavior as before (12s cadence, pauses when the tab is hidden, catches up on focus, never
//  fires on first sight), a fraction of the traffic.
// ══════════════════════════════════════════════════════════
import { useEffect, useRef } from 'react';

const INTERVAL_MS = 12000;
const _subs = new Map();          // id -> { keys:[], seen:{}, primed:false, cb:{current} }
let _conn = { supaUrl: '', supaKey: '', getToken: null };
let _timer = null, _kick = null, _busy = false, _again = false, _nextId = 1, _listening = false;

async function _check() {
  if (typeof document !== 'undefined' && document.hidden) return;
  if (_busy) { _again = true; return; }          // a screen mounted mid-request — run once more after
  if (!_conn.supaUrl || !_subs.size) return;
  const all = new Set();
  _subs.forEach(s => s.keys.forEach(k => all.add(k)));
  if (!all.size) return;
  _busy = true;
  try {
    let tok = null;
    try { tok = _conn.getToken ? await _conn.getToken() : null; } catch { tok = null; }
    const ks = [...all];
    const url = `${_conn.supaUrl}/rest/v1/app_storage?key=in.(${ks.map(encodeURIComponent).join(',')})&select=key,updated_at`;
    const r = await fetch(url, { headers: { apikey: _conn.supaKey, Authorization: `Bearer ${tok || _conn.supaKey}` } });
    const rows = await r.json();
    if (!Array.isArray(rows)) return;
    const stamp = {};
    rows.forEach(row => { stamp[row.key] = row.updated_at; });
    _subs.forEach(s => {
      const changed = [];
      s.keys.forEach(k => {
        if (!(k in stamp)) return;
        const prev = s.seen[k];
        s.seen[k] = stamp[k];
        if (s.primed && prev !== undefined && stamp[k] !== prev) changed.push(k);
      });
      s.primed = true;
      if (changed.length) { try { s.cb.current(changed); } catch {} }
    });
  } catch { /* offline / blip — next tick retries */ }
  finally { _busy = false; if (_again) { _again = false; _kickSoon(); } }
}

// Coalesce: several screens mounting at once → one priming request, not one each.
function _kickSoon() {
  if (_kick) return;
  _kick = setTimeout(() => { _kick = null; _check(); }, 250);
}

function _onWake() { if (typeof document === 'undefined' || !document.hidden) _kickSoon(); }

function _ensureRunning() {
  if (!_timer) _timer = setInterval(_check, INTERVAL_MS);
  if (!_listening && typeof window !== 'undefined') {
    window.addEventListener('focus', _onWake);
    document.addEventListener('visibilitychange', _onWake);
    _listening = true;
  }
}

function _stopIfIdle() {
  if (_subs.size) return;
  if (_timer) { clearInterval(_timer); _timer = null; }
  if (_listening && typeof window !== 'undefined') {
    window.removeEventListener('focus', _onWake);
    document.removeEventListener('visibilitychange', _onWake);
    _listening = false;
  }
}

// Same signature the HR/PM copies used: opts = { supaUrl, supaKey, getToken }.
export function useLiveKeys(keys, onChange, opts) {
  const supaUrl = opts && opts.supaUrl, supaKey = opts && opts.supaKey, getToken = opts && opts.getToken;
  const cbRef = useRef(onChange); cbRef.current = onChange;
  const tokRef = useRef(getToken); tokRef.current = getToken;
  const keyStr = (keys || []).filter(Boolean).join(',');
  useEffect(() => {
    if (!keyStr || !supaUrl) return;
    _conn = { supaUrl, supaKey, getToken: () => (tokRef.current ? tokRef.current() : null) };
    const id = _nextId++;
    _subs.set(id, { keys: keyStr.split(','), seen: {}, primed: false, cb: cbRef });
    _ensureRunning();
    _kickSoon();
    return () => { _subs.delete(id); _stopIfIdle(); };
  }, [keyStr, supaUrl, supaKey]);   // getToken read through a ref (identity can change each render)
}

export default useLiveKeys;
